import React from 'react';
import { Button, Notice, PageHeader, StatusDot } from '@origo/origo-ui';
import { ArrowClockwiseIcon } from '@phosphor-icons/react';
import { useBindings, useHealth, useMetrics, useSchedulesForBindings } from '../../api/hooks';
import { PanelError, PanelLoading } from '../common/PanelStates';
import { DIRECTION_LABEL, type SyncSchedule } from '../../api/types';
import { formatDateTime, scheduleCadence } from '../bindings/ScheduleEditor';
import styles from '../common/panel.module.css';

const TONE_CLASS: Record<'info' | 'success' | 'warning' | 'danger', string> = {
    info: styles.toneInfo,
    success: styles.toneSuccess,
    warning: styles.toneWarning,
    danger: styles.toneDanger,
};

const StatCard: React.FC<{ value: number; label: string; tone: 'info' | 'success' | 'warning' | 'danger' }> = ({
    value,
    label,
    tone,
}) => (
    <div className={styles.statCard}>
        <span className={`${styles.statValue} ${TONE_CLASS[tone]}`}>{value}</span>
        <span className={styles.statLabel}>{label}</span>
    </div>
);

const ScheduleStatusRow: React.FC<{ name: string; schedules: SyncSchedule[] }> = ({ name, schedules }) => (
    <div className={styles.scheduleStatusRow}>
        <span className={styles.scheduleStatusName}>{name}</span>
        {schedules.length === 0 ? (
            <span className={styles.hint}>Ingen tidsplaner</span>
        ) : (
            schedules.map((s) => (
                <div key={s.direction} className={styles.scheduleStatusLine}>
                    <StatusDot variant={s.enabled ? 'success' : 'warning'} size="sm" />
                    <span className={styles.scheduleStatusDir}>{DIRECTION_LABEL[s.direction]}</span>
                    <span className={styles.metaLabel}>{scheduleCadence(s)}</span>
                    <span className={styles.scheduleStatusNext}>
                        {s.enabled ? `neste: ${formatDateTime(s.nextDueUtc)}` : 'pauset'}
                    </span>
                </div>
            ))
        )}
    </div>
);

export const StatusPanel: React.FC = () => {
    const metrics = useMetrics();
    const health = useHealth();
    const bindings = useBindings(false);
    const scheduleOverview = useSchedulesForBindings(bindings.data);

    return (
        <>
            <PageHeader
                title="Status"
                description="Helse, synkkø og tidsstyrt synk."
                actions={
                    <Button
                        variant="ghost"
                        size="sm"
                        leftIcon={<ArrowClockwiseIcon size={16} weight="bold" />}
                        onClick={() => {
                            metrics.refetch();
                            health.refetch();
                            bindings.refetch();
                            scheduleOverview.refetch();
                        }}
                    >
                        Oppdater
                    </Button>
                }
            />

            <div className={styles.scroll}>
                <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>Helse</h3>
                    {health.isLoading && <PanelLoading label="Sjekker helse…" />}
                    {health.isError && <PanelError error={health.error} onRetry={() => health.refetch()} />}
                    {health.data && (
                        <div className={styles.stack}>
                            <div className={styles.healthRow}>
                                <StatusDot variant={health.data.healthz ? 'success' : 'error'} />
                                <span>Liveness (healthz): {health.data.healthz ? 'OK' : 'Nede'}</span>
                            </div>
                            <div className={styles.healthRow}>
                                <StatusDot variant={health.data.readyz ? 'success' : 'error'} />
                                <span>Readiness (readyz): {health.data.readyz ? 'Klar' : 'Ikke klar'}</span>
                            </div>
                        </div>
                    )}
                </div>

                <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>Synkroniseringskø</h3>
                    {metrics.isLoading && <PanelLoading label="Laster metrikker…" />}
                    {metrics.isError && <PanelError error={metrics.error} onRetry={() => metrics.refetch()} />}
                    {metrics.data && (
                        <div className={styles.statRow}>
                            <StatCard value={metrics.data.pending} label="Venter" tone="info" />
                            <StatCard value={metrics.data.processing} label="Behandles" tone="warning" />
                            <StatCard value={metrics.data.deadLettered} label="Dead-letter" tone="danger" />
                        </div>
                    )}
                    {metrics.data && metrics.data.deadLettered > 0 && (
                        <Notice variant="warning">
                            {metrics.data.deadLettered} forespørsel(er) i dead-letter. Sjekk logg for permanente feil.
                        </Notice>
                    )}
                </div>

                <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>Tidsstyrt synk pr kobling</h3>
                    {bindings.isLoading && <PanelLoading label="Laster koblinger…" />}
                    {bindings.isError && <PanelError error={bindings.error} onRetry={() => bindings.refetch()} />}
                    {bindings.data && bindings.data.length === 0 && (
                        <span className={styles.sectionHelp}>Ingen koblinger.</span>
                    )}
                    {bindings.data && bindings.data.length > 0 && (
                        <>
                            {scheduleOverview.isLoading && <PanelLoading label="Laster tidsplaner…" />}
                            {scheduleOverview.isError && (
                                <Notice variant="warning">Kunne ikke hente alle tidsplaner.</Notice>
                            )}
                            <div className={styles.list}>
                                {bindings.data.map((b) => (
                                    <ScheduleStatusRow
                                        key={b.bindingId}
                                        name={b.displayName}
                                        schedules={scheduleOverview.byBinding.get(b.bindingId) ?? []}
                                    />
                                ))}
                            </div>
                        </>
                    )}
                </div>

                <p className={styles.hint}>Oppdateres automatisk hvert 10–15 sekund.</p>
            </div>
        </>
    );
};
